import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LayoutDashboard } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { UserMenu } from "@/components/UserMenu";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useUserRole } from "@/hooks/useUserRole";
import logo from "@/assets/logo.webp";

interface HeaderProps {
  subtitle?: string;
  showDashboardButton?: boolean;
  showUserMenu?: boolean;
  showAuthButton?: boolean;
}

export function Header({ 
  subtitle = "Dialogue & Solution",
  showDashboardButton = true,
  showUserMenu = true,
  showAuthButton = true
}: HeaderProps) {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { data: userRole } = useUserRole();

  const handleDashboardClick = () => {
    if (userRole === 'admin') {
      navigate("/admin");
    } else if (userRole === 'mediator') {
      navigate("/mediateur/tableau-de-bord");
    } else if (userRole === 'seeker') {
      navigate("/trouver-un-mediateur");
    }
    // Do nothing if userRole is not yet loaded
  };

  return (
    <header className="bg-header/95 backdrop-blur-sm border-b border-border/50 sticky top-0 z-50">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div 
            className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity" 
            onClick={() => navigate('/')}
          >
            <img src={logo} alt="Trouve un médiateur" width={40} height={40} className="h-10 object-contain" />
            <div>
              <span className="text-xl font-bold text-foreground">Trouve un Médiateur</span>
              <p className="text-xs text-muted-foreground">{subtitle}</p>
            </div>
          </div>
          <nav aria-label="Navigation principale" className="flex items-center gap-2">
            <ThemeToggle />
            {authLoading ? (
              <div className="w-24 h-9" /> 
            ) : user ? (
              <>
                {showDashboardButton && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleDashboardClick}
                    className="flex items-center space-x-2"
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    <span className="hidden md:inline">Tableau de bord</span>
                  </Button>
                )}
                {showUserMenu && <UserMenu />}
              </>
            ) : (
              showAuthButton && (
                <Button onClick={() => navigate("/connexion")} className="bg-primary hover:bg-primary/90">
                  Se connecter
                </Button>
              )
            )}
          </nav>
        </div>
      </div>
    </header>
  );
}
